import { useEffect } from "react";
import "./App.css";
import i18n from "i18next";
import { useShallow } from "zustand/shallow";
import { UIState } from "./types/ui-types";
import { useUIStore } from "./stores/ui-store";
import { AppRoutes } from "./routes";
import styled from "styled-components";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

// Create a client
const queryClient = new QueryClient();

import {
  HttpRequestStatus,
  LoadingContainer,
} from "./components/loadingContainer";
import { Toast } from "primereact/toast";
import { AuthProvider, useAuth } from "./contexts/authContext";
import { ConfirmDialog } from "primereact/confirmdialog";
import SessionExpiredDialog from "./dialogs/sessionExpiredDialog";
import useConfiguration from "./hooks/useConfiguration";
import LanguageSelect from "./components/languageSelect";
import { ChatbotProvider } from "./contexts/chatbotContext";
import { ReportingCycleProvider } from "./contexts/reportingCycleContext";
import { UserProvider } from "./contexts/userContext";
import ChatbotButton from "./components/chatbotButton";
import ChatbotScreen from "./components/chatbotScreen";
import { useTheme } from "./hooks/useTheme";
import ThemeToggle from "./components/themeToggle";
import { AdminDataProvider } from "./contexts/adminDataContext";

const AppContainer = styled.div`
  height: 100vh;
  overflow: hidden;
`;

function App() {
  const [locale, toast, setEcomodMaxWeights, companyId, organizationId] =
    useUIStore(
      useShallow((state: UIState) => [
        state.locale,
        state.toastRef,
        state.setEcomodMaxWeights,
        state.companyId,
        state.organizationId,
      ]),
    );

  const {
    isLoggedIn,
    isLoadingUser,
    warnUnauthorized,
    warnForbidden,
    isAdmin,
    isAdminForSelectedCompanyOrOrganization,
    sessionExpired,
    confirmSessionExpired,
  } = useAuth();

  const { configurationData } = useConfiguration();

  // Initialize theme system
  useTheme();

  // Set the ecomod max weights to the store
  useEffect(() => {
    if (configurationData) {
      // find max weight keys containing "ecomodMaxWeight"
      const maxWeights = configurationData.filter((item) =>
        item.key.includes("ecomodMaxWeight"),
      );

      try {
        // check if maxWeights is empty
        if (maxWeights.length === 0) {
          throw new Error("No max weights found");
        }

        const parsedMaxWeights = maxWeights.map((item) => ({
          year: parseInt(item.key.split("_")[1]),
          value: parseInt(item.value),
        }));

        setEcomodMaxWeights(parsedMaxWeights);
      } catch (error) {
        console.error("Error parsing max weights:", error);
      }
    }
  }, [configurationData, setEcomodMaxWeights]);

  // Listen to unauthorized event from axios
  // and logout
  // TODO: can be later updated with refresh token system
  useEffect(() => {
    const handleUnauthorized = () => {
      warnUnauthorized();
    };

    const handleForbidden = () => {
      warnForbidden();
    };

    window.addEventListener("unauthorized", handleUnauthorized);
    window.addEventListener("forbidden", handleForbidden);

    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
      window.removeEventListener("forbidden", handleForbidden);
    };
  }, [warnUnauthorized, warnForbidden]);

  useEffect(() => {
    // set locale on startup
    i18n.changeLanguage(locale);
  }, [locale]);

  return (
    <LoadingContainer
      status={
        isLoadingUser ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
      }
    >
      <AppContainer className="app-container">
        <AdminDataProvider isAdmin={isAdmin}>
          <LanguageSelect />
          {isLoggedIn && <ChatbotButton />}
          {isLoggedIn && <ChatbotScreen />}
          <Toast ref={toast} position="bottom-left" />
          <SessionExpiredDialog
            show={sessionExpired}
            onAccept={confirmSessionExpired}
          />

          {/* Theme toggle in top right corner */}
          {import.meta.env.VITE_SHOW_THEME_TOGGLE === "true" && <ThemeToggle />}

          <AppRoutes
            isLoggedIn={isLoggedIn}
            isAdmin={isAdmin}
            isCompanyAdmin={isAdminForSelectedCompanyOrOrganization()}
          />
        </AdminDataProvider>
      </AppContainer>
    </LoadingContainer>
  );
}

// Wrap the App component with the AuthProvider so axios can use navigate, toast etc.
const WrappedApp = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <UserProvider>
        <ConfirmDialog />
        <ChatbotProvider>
          <ReportingCycleProvider>
            <App />
          </ReportingCycleProvider>
        </ChatbotProvider>
      </UserProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default WrappedApp;
